/**
 * The instant a plant day begins, on the plant's own clock (the server's plantclock DayStart): the day's date at the
 * production day's start, in the plant's zone, or on the evening before for a day that starts then ("-01:30": 22:30
 * on the date before). A dashboard pins its windows as plant days, as the lots rollup reads them, so a trend and a KPI
 * on one board cover the same hours (#994 review, MEDIUM-1). With no clock the server reads (plantClockOf), UTC
 * midnight, as the server reads an unconfigured plant. Pure.
 */
import { plantClockOf, type PlantTime } from "@/lib/shift-calendar";

/** How far a zone's wall clock runs ahead of UTC at an instant, in ms. */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(instant));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "NaN");
  return Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"), part("second")) - instant;
}

const HOUR_MS = 3_600_000;

/**
 * The instant a zone's wall clock reads `wall` (the reading as if it were UTC, in ms), by the server's rule
 * (plantclock wallInstant): every instant that reads that wall time is the wall time less an offset in effect around
 * it, and there are at most two such offsets, since a zone changes offset months apart. A candidate that reads the
 * wall time back is an occurrence, and the earliest wins: a time that happens twice (a fall-back overlap) is its first
 * occurrence. When neither reads, the time is in a spring-forward gap, which is the instant the gap ends. Stepping back
 * by the offset at the wall time itself, as this once did, lands on the wrong side of either change.
 */
function wallInstantMs(wall: number, zone: string): number {
  const reads = (t: number) => t + zoneOffsetMs(t, zone) === wall;
  const candidates = [-48 * HOUR_MS, 48 * HOUR_MS].map((probe) => wall - zoneOffsetMs(wall + probe, zone));
  const occurrences = candidates.filter(reads);
  if (occurrences.length > 0) return Math.min(...occurrences);
  // In a gap. The zone in effect at the earlier candidate ends where the clock jumps: find that instant, to the second.
  let lo = Math.min(...candidates);
  let hi = Math.max(...candidates);
  const before = zoneOffsetMs(lo, zone);
  if (zoneOffsetMs(hi, zone) === before) return wall - zoneOffsetMs(wall, zone); // a zone this reasoning does not fit
  while (hi - lo > 1000) {
    const mid = lo + Math.floor((hi - lo) / 2000) * 1000;
    if (zoneOffsetMs(mid, zone) === before) lo = mid;
    else hi = mid;
  }
  return hi;
}

/** The instant (epoch ms) plant day `day` (YYYY-MM-DD) begins; NaN for a day that does not read. */
export function plantDayStartMs(day: string, pt: PlantTime | null | undefined): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return NaN;
  const clock = plantClockOf(pt);
  // The wall reading the day starts at: its date's midnight moved by the signed day start (Date.UTC carries the minutes
  // over), so a day that starts the evening before begins on the date before.
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, clock?.start ?? 0);
  return clock ? wallInstantMs(wall, clock.zone) : wall;
}

/** The plant day after `day` (YYYY-MM-DD), on the calendar the day is written in. */
export function nextPlantDay(day: string): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
}
